<template>
  <!-- Switch das preferencias da barra (tema e idioma). O trilho mostra as
       duas opcoes apagadas; o botao desliza para o lado da opcao atual e leva
       o desenho dela em cor. Os desenhos vem pelos slots "off" e "on", que
       recebem `place` ("mark" no trilho, "thumb" no botao). -->
  <v-tooltip right>
    <template v-slot:activator="{ on: tip, attrs }">
      <button
        type="button"
        class="pref-switch"
        :class="{
          'pref-switch--on': on,
          'pref-switch--mini': mini,
          'pref-switch--rotate': rotate,
        }"
        :aria-label="label"
        v-bind="attrs"
        v-on="tip"
        @click="$emit('toggle')"
      >
        <span class="pref-switch__track" aria-hidden="true">
          <span class="pref-switch__mark">
            <slot name="off" place="mark" />
          </span>
          <span class="pref-switch__mark">
            <slot name="on" place="mark" />
          </span>
        </span>
        <span class="pref-switch__thumb" aria-hidden="true">
          <span class="pref-switch__icon pref-switch__icon--off">
            <slot name="off" place="thumb" />
          </span>
          <span class="pref-switch__icon pref-switch__icon--on">
            <slot name="on" place="thumb" />
          </span>
        </span>
      </button>
    </template>
    <span>{{ tooltip }}</span>
  </v-tooltip>
</template>

<script>
export default {
  name: "PrefSwitch",

  props: {
    // true = botao do lado direito (opcao "on").
    on: { type: Boolean, default: false },
    mini: { type: Boolean, default: false },
    // Gira o desenho na troca (sol e lua); as bandeiras so esmaecem.
    rotate: { type: Boolean, default: false },
    label: { type: String, required: true },
    tooltip: { type: String, required: true },
  },
};
</script>

<style scoped>
.pref-switch {
  --track-w: 64px;
  --track-h: 32px;
  --thumb: 26px;
  --pad: 3px;
  position: relative;
  flex-shrink: 0;
  width: var(--track-w);
  height: var(--track-h);
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
}

/* Na barra recolhida nao cabe o trilho inteiro: menor e sem as marcas. */
.pref-switch--mini {
  --track-w: 40px;
  --track-h: 24px;
  --thumb: 18px;
}

.pref-switch:focus-visible {
  outline: 2px solid var(--sb-accent, var(--pc-pink));
  outline-offset: 3px;
}

.pref-switch__track {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Marcas centradas onde o botao para: pad + (thumb - marca) / 2. */
  padding: 0 9px;
  border-radius: inherit;
  background: var(--sb-paper, var(--pc-paper));
  box-shadow: inset 0 0 0 1px rgba(var(--dk-ink-rgb, 35, 26, 46), 0.12);
  transition: background 0.3s ease;
}

.pref-switch--on .pref-switch__track {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.14);
}

/* As duas opcoes no trilho, apagadas: dizem o que ha do outro lado. */
.pref-switch__mark {
  display: inline-flex;
  width: 14px;
  height: 14px;
  opacity: 0.45;
  color: var(--sb-muted, var(--pc-muted));
  filter: grayscale(1);
}

.pref-switch--mini .pref-switch__mark {
  visibility: hidden;
}

.pref-switch__thumb {
  position: absolute;
  top: var(--pad);
  left: var(--pad);
  width: var(--thumb);
  height: var(--thumb);
  border-radius: 50%;
  background: var(--dk-surface, #fff);
  box-shadow: 0 2px 6px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.25);
  transition: transform 0.32s cubic-bezier(0.3, 0.7, 0.2, 1.2);
}

.pref-switch--on .pref-switch__thumb {
  transform: translateX(calc(var(--track-w) - var(--thumb) - 2 * var(--pad)));
}

.pref-switch__icon {
  position: absolute;
  inset: 4px;
  display: flex;
  transition: opacity 0.25s ease, transform 0.32s ease;
}

.pref-switch--mini .pref-switch__icon {
  inset: 3px;
}

.pref-switch__icon--on,
.pref-switch--on .pref-switch__icon--off {
  opacity: 0;
  transform: scale(0.6);
}

.pref-switch--rotate .pref-switch__icon--on {
  transform: rotate(-40deg) scale(0.6);
}

.pref-switch--rotate.pref-switch--on .pref-switch__icon--off {
  transform: rotate(60deg) scale(0.6);
}

.pref-switch--on .pref-switch__icon--on {
  opacity: 1;
  transform: none;
}

.pref-switch__mark ::v-deep svg,
.pref-switch__icon ::v-deep svg {
  width: 100%;
  height: 100%;
}

@media (prefers-reduced-motion: reduce) {
  .pref-switch__track,
  .pref-switch__thumb,
  .pref-switch__icon {
    transition: none;
  }
}
</style>
